import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { XMarkIcon } from '@heroicons/react/24/outline';

// redux
import { addArticle, editArticle } from '../../stores/articles';

// utilities
import { slugify, isValidSlug, uploadArticleBanner } from '../../utilities';

//components
import { SignedImage } from '../commons/signed.image';


const EMPTY = { resource_name: '', title: '', subtitle: '', image_url: '', url: '' };

/**
 * Modal for creating or editing a settings Article.
 *   - article == null  -> create mode (POST full object)
 *   - article provided -> edit mode (PUT only changed fields)
 */
export const ModalAddArticle = ({ isOpen, onClose, article = null }) => {

  const dispatch = useDispatch();
  const { creating, updating } = useSelector((state) => state.articles);
  const isEdit = Boolean(article);

  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState('');
  const [file, setFile] = useState(null);        // newly picked banner (not yet uploaded)
  const [uploading, setUploading] = useState(false);

  // pre-fill on open
  useEffect(() => {
    if (isOpen) {
      setForm(article ? { ...EMPTY, ...article } : EMPTY);
      setSlugTouched(false);
      setFile(null);
      setError('');
    }
  }, [isOpen, article]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

   // auto-generate slug from title until the admin edits it manually (create only)
  const onTitleChange = (value) => {
    setForm((f) => ({
      ...f,
      title: value,
      ...(!isEdit && !slugTouched && { resource_name: slugify(value) }),
    }));
  };

  const handleSubmit = async () => {
    setError('');

    if (isEdit) {
      // diff: only send fields the admin actually changed AND that are non-empty.
      // Backend rejects empty strings (e.g. title must be >= 1 char), so never send them.
      const changes = {};
      ['title', 'subtitle', 'url'].forEach((k) => {
        const next = (form[k] ?? '').trim();
        const prev = (article[k] ?? '').trim();
        if (next !== prev && next !== '') changes[k] = form[k];
      });

      try {
        setUploading(true);
        if (file) {
          const uploaded = await uploadArticleBanner(article.resource_name, file);
          changes.image_url = uploaded.key; // new banner key; only send when a file was picked
        }
        if (Object.keys(changes).length === 0) {
          setError('No changes to save.');
          return;
        }
        await dispatch(editArticle({ resource_name: article.resource_name, changes })).unwrap();
        toast.success('Article updated');
        onClose();
      } catch (err) {
        setError(err?.message || 'Failed to update article');
      } finally {
        setUploading(false);
      }
      return;
    }

    // create: all fields required + slug valid
    if (!form.title || !form.subtitle || !form.url) {
      setError('Title, subtitle and article URL are required.');
      return;
    }
    if (!isValidSlug(form.resource_name)) {
      setError('Resource name must be lowercase words separated by single dashes.');
      return;
    }
    if (!file && !form.image_url) {
      setError('Please choose a banner image.');
      return;
    }

    try {
      setUploading(true);
      let image_url = form.image_url;
      if (file) {
        const uploaded = await uploadArticleBanner(form.resource_name, file);
        image_url = uploaded.url;
      }
      await dispatch(addArticle({ ...form, image_url })).unwrap();
      toast.success('Article created');
      onClose();
    } catch (err) {
      setError(err?.status === 409 ? 'That resource name is already used.' : (err?.message || 'Failed to create article'));
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  const saving = creating || updating || uploading;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Article' : 'Add New Article'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Title (Title Name of the article)</label>
            <input type="text" value={form.title} onChange={(e) => onTitleChange(e.target.value)}
             placeholder='e.g. Tiga Gangguan Emosi ketika "Blackout"'
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Resource name (slug)</label>
            <input type="text" value={form.resource_name}
              onChange={(e) => { setSlugTouched(true); setField('resource_name', e.target.value); }}
              disabled={isEdit}
              placeholder="e.g. tiga-gangguan-emosi-ketika-blackout"
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100" />
            {isEdit && <p className="mt-1 text-xs text-gray-400">Resource name can't be changed after creation.</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Subtitle (Description of the article)</label>
            <input type="text" value={form.subtitle} onChange={(e) => setField('subtitle', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Banner image</label>
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1 block w-full text-sm text-gray-600 file:mr-3 file:rounded-md file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 hover:file:bg-primary-100"
            />
            {file ? (
              <img
                src={URL.createObjectURL(file)}
                alt="Banner preview"
                className="mt-2 h-32 w-full object-cover rounded-md border border-gray-200"
              />
            ) : form.image_url ? (
              <SignedImage imageKey={form.image_url} alt="Banner preview"
                className="mt-2 h-32 w-full object-cover rounded-md border border-gray-200" />
            ) : null
            }

            <p className="mt-1 text-xs text-gray-400">Shown on the client app homepage. PNG, JPG, or WebP.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Article URL</label>
            <input type="url" value={form.url} onChange={(e) => setField('url', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="flex justify-end gap-2 p-4 border-t">
          <button onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
          <button onClick={handleSubmit} disabled={saving}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50">
            {uploading ? 'Uploading...' : saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Article')}
          </button>
        </div>
      </div>
    </div>
  );
};
